import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { syncAPI, oauthAPI, subscriptionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackBackup, trackError, trackEngagement, trackOAuth, trackSubscription } from '../utils/analytics';
import { isInAppBrowser, isAndroid, copyToClipboard, getInAppBrowserType } from '../utils/browserDetection';
import {
  CloudArrowUpIcon,
  PhotoIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  TrophyIcon,
  ArrowUpTrayIcon,
  FilmIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import Footer from './Footer';
import InstagramAccountsManager from './InstagramAccountsManager';
import OnboardingWizard from './OnboardingWizard';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Refresh user profile on mount to get latest subscription data
  useEffect(() => {
    refreshProfile();
    // Track dashboard view
    trackEngagement.viewDashboard();
  }, []);
  
  // Check for OAuth errors and success messages in URL
  useEffect(() => {
    const error = searchParams.get('error');
    const errorType = searchParams.get('error_type');
    const instagramConnected = searchParams.get('instagram_connected');
    const googleConnected = searchParams.get('google_connected');
    const via = searchParams.get('via');
    const stripeSuccess = searchParams.get('success');
    const stripeCanceled = searchParams.get('canceled');
    
    // Handle successful connections
    if (instagramConnected === 'true') {
      const message = via === 'facebook' 
        ? 'Instagram connected successfully via Facebook!' 
        : 'Instagram connected successfully!';
      toast.success(message);
      
      // Track Instagram connection success
      console.log('🎯 Instagram connected - firing analytics...');
      trackOAuth.connectInstagram(true);
      console.log('✅ Instagram analytics tracking completed');
      
      // Set flag that Instagram was just connected
      setIsInstagramJustConnected(true);
      
      // Store connection time in session storage
      sessionStorage.setItem('instagram_connected_time', Date.now().toString());
      
      queryClient.invalidateQueries(['oauth-status']);
      queryClient.invalidateQueries(['instagram-accounts']); // Refresh Instagram accounts list
      
      // Check if both connections are active after status refresh
      setTimeout(async () => {
        try {
          const oauthStatus = await oauthAPI.getStatus();
          if (oauthStatus.data?.instagram_connected && oauthStatus.data?.google_connected) {
            console.log('🎯 Both connections active - firing UserConnected event');
            trackOAuth.userConnected();
          }
        } catch (statusError) {
          console.warn('Could not check OAuth status:', statusError);
        }
      }, 1500); // Wait for OAuth status to update
      
      // Refresh Instagram posts immediately after connection
      setTimeout(() => {
        // The posts query will auto-refresh when oauth-status updates
        queryClient.invalidateQueries(['instagram-posts']);
        queryClient.invalidateQueries(['instagram-accounts']);
      }, 1000); // Small delay to ensure OAuth status is updated
      
      navigate('/dashboard', { replace: true });
      return;
    }
    
    if (googleConnected === 'true') {
      toast.success('Google Drive connected successfully!');
      
      // Track Google Drive connection success
      console.log('🎯 Google Drive connected - firing analytics...');
      trackOAuth.connectGoogleDrive(true);
      console.log('✅ Google Drive analytics tracking completed');
      
      queryClient.invalidateQueries(['oauth-status']);
      
      // Check if both connections are active after status refresh
      setTimeout(async () => {
        try {
          const oauthStatus = await oauthAPI.getStatus();
          if (oauthStatus.data?.instagram_connected && oauthStatus.data?.google_connected) {
            console.log('🎯 Both connections active - firing UserConnected event');
            trackOAuth.userConnected();
          }
        } catch (statusError) {
          console.warn('Could not check OAuth status:', statusError);
        }
      }, 1500); // Wait for OAuth status to update
      
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // Handle Google Drive permission error
    const googleError = searchParams.get('google_error');
    if (googleError === 'insufficient_permissions') {
      toast.error('Google Drive connection failed: Please grant Drive permission to enable backups. Make sure to check the box for Drive access when connecting.', {
        autoClose: 10000
      });
      trackOAuth.connectGoogleDrive(false);
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // Handle error_type even without error message
    if (errorType === 'facebook_page_required' || errorType === 'unsupported_account') {
      setFacebookPageError(true);
      navigate('/dashboard', { replace: true });
      return;
    }
    
    if (error) {
      if (errorType === 'personal_account') {
        // Show error for personal account
        toast.error(
          <div>
            <h4 className="font-semibold mb-2">Personal Account Not Supported</h4>
            <p className="text-sm mb-3">Instagram only allows API access for Professional accounts (Business or Creator).</p>
            <p className="text-sm">Please convert to a Professional account in your Instagram settings, then try again.</p>
          </div>,
          {
            autoClose: false,
            closeButton: true,
            style: { maxWidth: '400px' }
          }
        );
      } else if (error && error.includes('Facebook Page')) {
        // Handle Facebook Page error
        setFacebookPageError(true);
        // Don't show toast for Facebook Page errors
      } else {
        toast.error(error);
      }
      
      // Clean up URL
      navigate('/dashboard', { replace: true });
    }
    
    // Handle Stripe checkout success
    if (stripeSuccess === 'true') {
      refreshProfile(); // Refresh user profile to get updated subscription
      
      // Track successful purchase with transaction details
      console.log('🎯 Subscription purchase successful - firing analytics...');
      
      // Fetch subscription details after a short delay to ensure backend has processed
      setTimeout(async () => {
        try {
          const subscriptionResponse = await subscriptionAPI.getCurrentSubscription();
          const subscription = subscriptionResponse.data;
          
          // Extract transaction details
          const transactionId = subscription.id?.toString() || `vaulto_${Date.now()}`;
          const planName = subscription.tier || 'Subscription';
          const price = subscription.amount ? parseFloat(subscription.amount) : 0;
          const itemId = subscription.tier || planName;
          
          // Track purchase with proper GA4 format
          trackSubscription.purchase(transactionId, planName, price, itemId);
          console.log('✅ Purchase analytics tracking completed with transaction details');
          
          // Show welcome modal with upgrade details
          setUpgradedTier(planName);
          setShowUpgradeWelcome(true);
          
          // Auto-dismiss after 10 seconds
          setTimeout(() => {
            setShowUpgradeWelcome(false);
            setUpgradedTier(null);
          }, 10000);
          
          // Refresh stats to show updated limits
          refetchStats();
        } catch (error) {
          console.error('Error fetching subscription for purchase tracking:', error);
          // Fallback to generic tracking if subscription fetch fails
          trackSubscription.purchase(`vaulto_${Date.now()}`, 'Subscription', 0, null);
          // Still show welcome message
          setUpgradedTier('Premium');
          setShowUpgradeWelcome(true);
        }
      }, 1000); // Wait 1 second for backend to process subscription
      
      // Clean up URL
      navigate('/dashboard', { replace: true });
    }
    
    // Handle Stripe checkout cancellation
    if (stripeCanceled === 'true') {
      toast.info('Subscription cancelled. You can upgrade anytime from the Settings page.');
      // Clean up URL - redirect to pricing page
      navigate('/pricing', { replace: true });
    }
  }, [searchParams, navigate, queryClient, refreshProfile]);
  
  // State for various UI controls
  const [syncLoading, setSyncLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('all');  // Media type filter: all, image, video
  const [contentType, setContentType] = useState('all');  // Content type filter: all, posts, reels, stories
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [facebookPageError, setFacebookPageError] = useState(false);
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [showInAppBrowserModal, setShowInAppBrowserModal] = useState(false);
  const [showInAppBrowserSuggestion, setShowInAppBrowserSuggestion] = useState(false);
  const [showInstagramExplanationModal, setShowInstagramExplanationModal] = useState(false);
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [isInstagramJustConnected, setIsInstagramJustConnected] = useState(false);
  const [showUpgradeWelcome, setShowUpgradeWelcome] = useState(false);
  const [upgradedTier, setUpgradedTier] = useState(null);
  const [showGoogleInstructions, setShowGoogleInstructions] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyReason, setSurveyReason] = useState('');
  const [surveyComment, setSurveyComment] = useState('');
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [showGoogleDriveRequiredModal, setShowGoogleDriveRequiredModal] = useState(false);
  const [showAbandonedFlowBanner, setShowAbandonedFlowBanner] = useState(false);
  
  // Survey options for Instagram abandonment
  const SURVEY_OPTIONS = [
    { id: 'personal_account', label: "I have a personal account (not business/creator)" },
    { id: 'forgot_password', label: "I don't remember my Instagram login details or password" },
    { id: 'too_confusing', label: "It was too confusing" },
    { id: 'got_error', label: "I got an error" },
    { id: 'changed_mind', label: "I changed my mind" },
    { id: 'do_later', label: "I'll do it later" },
    { id: 'other', label: "Other" }
  ];
  
  // Initialize Instagram connection state from session storage
  useEffect(() => {
    const connectionTime = sessionStorage.getItem('instagram_connected_time');
    if (connectionTime) {
      const timeSinceConnection = Date.now() - parseInt(connectionTime);
      if (timeSinceConnection < 120000) { // Within 2 minutes
        setIsInstagramJustConnected(true);
      }
    }
  }, []);

  // Handle survey submission
  const handleSurveySubmit = async () => {
    if (!surveyReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setSurveySubmitting(true);
    try {
      await oauthAPI.submitSurveyResponse(surveyReason, surveyComment || null);
      toast.success('Thank you for your feedback!');
      // Mark survey as shown to prevent any issues if backend call fails
      localStorage.setItem('survey_last_shown', Date.now().toString());
      setShowSurveyModal(false);
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSurveySubmitting(false);
    }
  };

  const handleSurveySkip = () => {
    // Store the time when survey was dismissed to prevent showing too frequently
    localStorage.setItem('survey_last_shown', Date.now().toString());
    setShowSurveyModal(false);
  };

  // Check if enough time has passed since last survey display (30 minutes minimum)
  const canShowSurvey = () => {
    const lastShown = localStorage.getItem('survey_last_shown');
    if (!lastShown) return true;
    const timeSinceLastShown = Date.now() - parseInt(lastShown);
    const THIRTY_MINUTES = 30 * 60 * 1000; // 30 minutes in milliseconds
    return timeSinceLastShown >= THIRTY_MINUTES;
  };

  // Mark survey as shown (for throttling purposes)
  const markSurveyShown = () => {
    localStorage.setItem('survey_last_shown', Date.now().toString());
  };

  const POSTS_PER_PAGE = 36; // Show more posts per page for better UX

  // Fetch dashboard stats with auto-refresh
  // Faster refresh (3s) during active sync, responsive refresh (10s) when idle
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => syncAPI.getDashboardStats().then(res => res.data),
    refetchInterval: syncLoading ? 3000 : 10000, // 3s during sync, 10s idle
  });

  // Fetch OAuth status
  const { data: oauthStatus, isLoading: oauthLoading, error: oauthError } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: () => oauthAPI.getStatus().then(res => {
      console.log('OAuth status response:', res.data);
      return res.data;
    }),
    onError: (error) => {
      console.error('Error fetching OAuth status:', error);
    }
  });

  // Check for abandoned Instagram connection flow
  useEffect(() => {
    if (oauthStatus) {
      const connectionAttempted = localStorage.getItem('instagram_connection_attempted');
      const abandonedBannerDismissed = sessionStorage.getItem('abandoned_banner_dismissed');
      
      // Show banner if:
      // 1. Google is connected but Instagram is not
      // 2. User has previously attempted to connect Instagram
      // 3. Banner hasn't been dismissed this session
      // 4. Wizard is not currently showing
      if (
        oauthStatus.google_connected && 
        !oauthStatus.instagram_connected && 
        connectionAttempted && 
        !abandonedBannerDismissed &&
        !showOnboardingWizard
      ) {
        // Only show if attempt was within the last 7 days
        const attemptTime = parseInt(connectionAttempted);
        const daysSinceAttempt = (Date.now() - attemptTime) / (1000 * 60 * 60 * 24);
        if (daysSinceAttempt < 7) {
          setShowAbandonedFlowBanner(true);
        }
      }
      
      // Clear the connection attempt flag if Instagram is now connected
      if (oauthStatus.instagram_connected) {
        localStorage.removeItem('instagram_connection_attempted');
        setShowAbandonedFlowBanner(false);
      }
    }
  }, [oauthStatus, showOnboardingWizard]);

  // Check if we should show the Instagram abandonment survey or in-app browser suggestion
  useEffect(() => {
    const checkSurveyOrInAppBrowser = async () => {
      // Don't show survey if it was shown recently (within 30 minutes)
      if (!canShowSurvey()) return;
      
      try {
        const response = await oauthAPI.shouldShowSurvey();
        if (response.data.should_show) {
          // If user is in an in-app browser, show browser suggestion instead of survey
          if (isInAppBrowser() && !oauthStatus?.instagram_connected) {
            setTimeout(() => {
              markSurveyShown();
              setShowInAppBrowserSuggestion(true);
            }, 1500);
          } else {
            // Delay showing survey slightly so it doesn't appear immediately on page load
            setTimeout(() => {
              markSurveyShown();
              setShowSurveyModal(true);
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error checking survey status:', error);
      }
    };
    checkSurveyOrInAppBrowser();
  }, [oauthStatus?.instagram_connected]); // Run when oauth status changes

  // Re-check survey when user returns to page (e.g., clicked back from Instagram OAuth)
  useEffect(() => {
    const checkSurveyWithDelay = () => {
      // Don't show survey if it was shown recently (within 30 minutes)
      if (!canShowSurvey()) return;
      
      // Small delay to ensure backend has processed the attempt
      setTimeout(() => {
        oauthAPI.shouldShowSurvey().then(response => {
          if (response.data.should_show) {
            markSurveyShown();
            // If user is in an in-app browser, show browser suggestion instead of survey
            if (isInAppBrowser() && !oauthStatus?.instagram_connected) {
              setShowInAppBrowserSuggestion(true);
            } else {
              setShowSurveyModal(true);
            }
          }
        }).catch(console.error);
      }, 500);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSurveyWithDelay();
      }
    };

    const handleFocus = () => {
      checkSurveyWithDelay();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [oauthStatus?.instagram_connected]);

  // Onboarding Wizard: Only show when wizard=true param is present
  useEffect(() => {
    const wizardParam = searchParams.get('wizard');
    const wizardSkipped = sessionStorage.getItem('wizard_skipped');
    const wizardCompleted = localStorage.getItem('wizard_completed');
    const wizardModeActive = sessionStorage.getItem('wizard_mode_active');
    
    // Only show wizard if explicitly requested via URL param OR if wizard mode was active (coming back from OAuth)
    if (wizardParam === 'true' || wizardModeActive === 'true') {
      // Clean up wizard=true param from URL if present
      if (wizardParam === 'true') {
        navigate('/dashboard', { replace: true });
      }
      
      // Don't show wizard if user explicitly skipped it this session or already completed
      if (wizardSkipped === 'true' || wizardCompleted === 'true') {
        setShowOnboardingWizard(false);
        return;
      }
      
      // Show wizard if connections not complete
      if (oauthStatus && (!oauthStatus.instagram_connected || !oauthStatus.google_connected)) {
        setShowOnboardingWizard(true);
      } else if (oauthStatus?.instagram_connected && oauthStatus?.google_connected) {
        // Both connected - close wizard
        setShowOnboardingWizard(false);
      }
    } else {
      // Wizard not requested - don't show it
      setShowOnboardingWizard(false);
    }
  }, [searchParams, navigate, oauthStatus]);

  // Fetch Instagram posts with pagination and filtering
  const { data: postsResponse, isLoading: postsLoading, refetch: refetchPosts, error: postsError } = useQuery({
    queryKey: ['instagram-posts', currentPage, filterType, contentType, searchQuery, selectedAccountId],
    queryFn: () => syncAPI.getPosts({
      limit: POSTS_PER_PAGE,
      offset: (currentPage - 1) * POSTS_PER_PAGE,
      media_type: filterType === 'all' ? undefined : filterType,
      product_type: contentType === 'all' ? undefined : contentType,
      search: searchQuery || undefined,
      account_id: selectedAccountId || undefined,
    }).then(res => {
      console.log('Posts API response:', res.data);
      return res.data;
    }),
    enabled: oauthStatus?.instagram_connected,
    keepPreviousData: true, // Keep previous data while loading new page
    onError: (error) => {
      console.error('Error fetching posts:', error);
    }
  });

  const posts = postsResponse?.posts || [];
  const totalPosts = postsResponse?.total || 0;
  
  // Clear "just connected" flag when posts are loaded
  useEffect(() => {
    if (isInstagramJustConnected && totalPosts > 0) {
      setIsInstagramJustConnected(false);
    }
  }, [isInstagramJustConnected, totalPosts]);
  
  // Check if Instagram was recently connected (within last 30 seconds for loading, 2 minutes for persistence)
  const isRecentlyConnected = useCallback(() => {
    const lastConnectedTime = sessionStorage.getItem('instagram_connected_time');
    if (!lastConnectedTime) return false;
    
    const timeSinceConnection = Date.now() - parseInt(lastConnectedTime);
    return timeSinceConnection < 30000; // 30 seconds for loading animation
  }, []);
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);

  // Check for new posts (for free users)
  const { data: newPostsData, refetch: refetchNewPosts } = useQuery({
    queryKey: ['new-posts-check', user?.subscription_tier],
    queryFn: () => syncAPI.checkNewPosts().then(res => res.data),
    enabled: oauthStatus?.instagram_connected && user?.subscription_tier === 'free',
    refetchInterval: 60000, // Check every minute for free users
  });

  // Debug logging
  console.log('Dashboard state:', {
    oauthStatus,
    oauthLoading,
    oauthError,
    postsResponse,
    posts,
    totalPosts,
    postsLoading,
    postsError,
    newPostsData
  });

  // Auto-refresh posts if Instagram is connected but no posts are shown
  // Only when NO filters are active (otherwise 0 posts is expected for certain filters)
  useEffect(() => {
    const checkAndRefreshPosts = async () => {
      // Skip auto-refresh if any filter is active - 0 results might be expected
      if (filterType !== 'all' || contentType !== 'all' || searchQuery) {
        return;
      }
      
      // Check if Instagram is connected, posts have loaded, but there are no posts
      if (oauthStatus?.instagram_connected && !postsLoading && totalPosts === 0) {
        // Check if we recently attempted (within last 30 seconds)
        const lastAttempt = sessionStorage.getItem('posts_refresh_attempted');
        const now = Date.now();
        
        if (lastAttempt && (now - parseInt(lastAttempt)) < 30000) {
          console.log('Recently attempted refresh, waiting...');
          return;
        }
        
        console.log('No posts found, attempting auto-refresh...');
        
        // Mark attempt with timestamp
        sessionStorage.setItem('posts_refresh_attempted', now.toString());
        
        try {
          // Call the refresh endpoint
          const response = await syncAPI.refreshInstagramPosts();
          console.log('Refresh response:', response.data);
          
          if (response.data.post_count > 0) {
            // Posts were fetched, refetch the posts query
            toast.success(`Loaded ${response.data.post_count} Instagram posts`);
            refetchPosts();
            // Clear the flag on success
            sessionStorage.removeItem('posts_refresh_attempted');
          } else if (response.data.new_posts > 0) {
            // New posts were added, refetch
            toast.info('New posts detected, refreshing...');
            refetchPosts();
          }
        } catch (error) {
          console.error('Error refreshing posts:', error);
        }
      }
    };

    // Run check immediately and then periodically
    checkAndRefreshPosts();
    const interval = setInterval(checkAndRefreshPosts, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, [oauthStatus, postsLoading, totalPosts, refetchPosts, filterType, contentType, searchQuery]);

  // Clear refresh attempt when Instagram is newly connected
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('instagram_connected') === 'true') {
      console.log('Instagram newly connected, clearing refresh flag');
      sessionStorage.removeItem('posts_refresh_attempted');
    }
  }, []);

  const handleStartSync = async () => {
    if (!oauthStatus?.instagram_connected) {
      toast.error(t('dashboard.getStarted.step1.description'));
      return;
    }
    if (!oauthStatus?.google_connected) {
      toast.error(t('dashboard.getStarted.step2.description'));
      return;
    }

    setSyncLoading(true);
    // Track manual backup started
    trackBackup.manualBackupStarted();
    
    try {
      await syncAPI.startInstagramSync();
      toast.success(t('dashboard.sync.started'));
      
      // Start polling for sync status
      pollSyncStatus();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to start sync';
      // Track backup failure
      trackBackup.manualBackupFailed(errorMessage);
      trackError.backupError('manual_backup_start', errorMessage);
      toast.error(errorMessage);
      setSyncLoading(false);
    }
  };

  // Poll sync status until complete
  const pollSyncStatus = async () => {
    const maxAttempts = 60; // Max 5 minutes (60 * 5 seconds)
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await syncAPI.getSyncStatus();
        const status = response.data;
        
        // Update stats every check to show real-time progress
        refetchStats();
        
        // Also refresh posts every 3 checks (~9 seconds) to update backup indicators
        if (attempts % 3 === 0) {
          refetchPosts();
        }
        
        if (status.is_syncing === false || attempts >= maxAttempts) {
          // Sync complete or timed out
          setSyncLoading(false);
          
          // Final refetch of all data to ensure UI is fully updated
          refetchStats();
          refetchPosts();
          queryClient.invalidateQueries(['oauth-status']);
          queryClient.invalidateQueries(['new-posts-check']);
          
          if (status.is_syncing === false && status.sync_job?.status === 'completed') {
            const postsCount = status.sync_job?.posts_processed || 0;
            // Track successful backup completion
            trackBackup.manualBackupCompleted(postsCount);
            
            // Check for free tier limit message
            if (status.sync_job?.error_message && status.sync_job.error_message.includes('Free tier limit')) {
              toast.warning(status.sync_job.error_message, { 
                autoClose: 10000,  // Show for 10 seconds
                style: { maxWidth: '500px' }  // Allow wider toast for the message
              });
            } else {
              toast.success(t('dashboard.sync.completed'));
            }
          } else if (attempts >= maxAttempts) {
            toast.warning(t('dashboard.sync.takingLonger'));
          }
        } else {
          // Still syncing, check again in 3 seconds for faster updates
          attempts++;
          
          // Show progress toast periodically
          if (attempts % 4 === 0 && status.sync_job) {
            const processed = status.sync_job.posts_processed || 0;
            const total = status.sync_job.posts_total || 0;
            if (total > 0) {
              toast.info(t('dashboard.sync.progress', { processed, total }), {
                autoClose: 2000,
                toastId: 'sync-progress'
              });
            }
          }
          
          setTimeout(checkStatus, 3000);
        }
      } catch (error) {
        console.error('Error checking sync status:', error);
        setSyncLoading(false);
      }
    };
    
    // Start checking after 2 seconds
    setTimeout(checkStatus, 2000);
  };

  const handleRefreshPosts = async () => {
    try {
      setRefreshLoading(true);
      await syncAPI.refreshInstagramPosts();
      toast.success(t('common.success'));
      
      // Refetch posts data and new posts check
      refetchPosts();
      refetchNewPosts();
      queryClient.invalidateQueries(['new-posts-check']);
      
    } catch (error) {
      console.error('Failed to refresh posts:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh posts');
    } finally {
      setRefreshLoading(false);
    }
  };

  // Onboarding Wizard completion handler
  const handleWizardComplete = () => {
    setShowOnboardingWizard(false);
    sessionStorage.removeItem('wizard_mode_active');
    localStorage.setItem('wizard_completed', 'true');
    toast.success(t('wizard.completed'));
    // Refresh all data
    queryClient.invalidateQueries(['oauth-status']);
    queryClient.invalidateQueries(['dashboard-stats']);
    refetchPosts();
  };

  // Onboarding Wizard skip handler - user wants to see dashboard without completing wizard
  const handleWizardSkip = () => {
    setShowOnboardingWizard(false);
    // Only skip for this session - will show again on next login
    sessionStorage.setItem('wizard_skipped', 'true');
  };

  // Show the account type selection modal before Instagram OAuth
  const handleConnectInstagramClick = () => {
    // Check if Google Drive is connected first
    if (!oauthStatus?.google_connected) {
      setShowGoogleDriveRequiredModal(true);
      return;
    }
    setShowAccountTypeModal(true);
  };

  // Actually initiate Instagram OAuth after account type is confirmed
  const handleConnectInstagram = async (accountType = 'business') => {
    setShowAccountTypeModal(false);
    setShowInstagramExplanationModal(true);
    // Store accountType for later use when user confirms
    sessionStorage.setItem('pending_instagram_account_type', accountType);
  };

  // Proceed with Instagram OAuth after user confirms
  const proceedWithInstagramOAuth = async () => {
    setShowInstagramExplanationModal(false);
    const accountType = sessionStorage.getItem('pending_instagram_account_type') || 'business';
    sessionStorage.removeItem('pending_instagram_account_type');
    
    try {
      const response = await oauthAPI.getInstagramAuthUrl(accountType);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Handle "I have a personal account" - show info modal about switching to Creator
  const handlePersonalAccountHelp = async () => {
    setShowAccountTypeModal(false);
    // Log this click for analytics
    try {
      await oauthAPI.logPersonalAccountClick();
    } catch (e) {
      // Ignore errors - logging shouldn't block navigation
    }
    setShowPersonalInfoModal(true);
  };

  // Proceed to Instagram OAuth after user acknowledges they need to switch to Creator
  const handlePersonalAccountContinue = () => {
    setShowPersonalInfoModal(false);
    handleConnectInstagram('business'); // Uses same OAuth flow
  };

  const handleConnectGoogle = () => {
    // Show instructions modal before redirecting to Google
    setShowGoogleInstructions(true);
  };

  const proceedWithGoogleConnection = async () => {
    try {
      const response = await oauthAPI.getGoogleAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get icon for content type - Updated to handle Instagram API values
  const getContentTypeIcon = (mediaType, size = 'h-4 w-4') => {
    if (!mediaType) return <PhotoIcon className={`${size} text-green-400`} />;
    
    const type = mediaType.toLowerCase();
    
    if (type.includes('video')) {
      return <PlayIcon className={`${size} text-blue-400`} />;
    } else if (type.includes('carousel')) {
      return <PhotoIcon className={`${size} text-purple-400`} />;
    } else if (type.includes('image')) {
      return <PhotoIcon className={`${size} text-green-400`} />;
    } else {
      return <PhotoIcon className={`${size} text-green-400`} />;
    }
  };

  // Get content type label - Updated to handle Instagram API values
  const getContentTypeLabel = (mediaType) => {
    if (!mediaType) return t('dashboard.contentTypes.photo');
    
    const type = mediaType.toLowerCase();
    
    if (type.includes('video')) {
      return t('dashboard.contentTypes.video');
    } else if (type.includes('carousel')) {
      return t('dashboard.contentTypes.album');
    } else if (type.includes('image')) {
      return t('dashboard.contentTypes.photo');
    } else {
      return t('dashboard.contentTypes.photo');
    }
  };

  // Check if media type is video
  const isVideo = (mediaType) => {
    return mediaType && mediaType.toLowerCase().includes('video');
  };

  // Handle media click - videos open in modal, photos open in new tab
  const handleMediaClick = (item) => {
    if (isVideo(item.media_type) && item.media_url) {
      setSelectedVideo(item);
    } else if (item.media_url) {
      window.open(item.media_url, '_blank');
    }
  };

  // Get backup status indicator - assumes backup data comes with posts
  const getBackupStatusIndicator = (post) => {
    // For now, we'll simulate backup status based on post age or other factors
    // In real implementation, this would come from the API
    const hasBackup = post.backup_status === 'completed' || post.is_backed_up;
    const isProcessing = post.backup_status === 'processing' || post.backup_status === 'pending';
    
    if (hasBackup) {
      return (
        <div className="absolute top-2 right-2 z-10 bg-green-500/90 backdrop-blur-sm rounded-full p-1.5" title={t('dashboard.backupStatus.backedUp')}>
          <CheckCircleIcon className="h-4 w-4 text-white" />
        </div>
      );
    } else if (isProcessing) {
      return (
        <div className="absolute top-2 right-2 z-10 bg-yellow-500/90 backdrop-blur-sm rounded-full p-1.5" title={t('dashboard.backupStatus.inProgress')}>
          <ClockIcon className="h-4 w-4 text-white animate-pulse" />
        </div>
      );
    } else {
      return (
        <div className="absolute top-2 right-2 z-10 bg-gray-500/90 backdrop-blur-sm rounded-full p-1.5" title={t('dashboard.backupStatus.pending')}>
          <ArrowPathIcon className="h-4 w-4 text-white" />
        </div>
      );
    }
  };

  const handleFilterChange = (filter) => {
    setFilterType(filter);
    setCurrentPage(1);
  };

  const handleContentTypeChange = (type) => {
    setContentType(type);
    setCurrentPage(1);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (statsLoading || oauthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Account Type Selection Modal */}
      {showAccountTypeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/80 backdrop-blur-sm" onClick={() => setShowAccountTypeModal(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white/10 backdrop-blur-lg border border-white/20 shadow-2xl rounded-2xl">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  What type of Instagram account do you have?
                </h3>
                <p className="text-sm text-gray-300">
                  Vaulto requires a Creator or Business account to access your content.
                </p>
                {/* Official API Badge */}
                <div className="flex justify-center mt-3">
                  <div className="flex items-center px-3 py-1.5 bg-green-500/10 rounded-full border border-green-500/30">
                    <ShieldCheckIcon className="h-4 w-4 text-green-400 mr-2" />
                    <span className="text-xs text-green-300 font-medium">Official Instagram API</span>
                  </div>
                </div>
              </div>

              {/* Loss aversion warning */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-200 text-xs">
                    Every day, creators lose content to hacks, suspensions, and accidental deletions. Protect your work now.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleConnectInstagram('business')}
                  className="w-full flex items-center p-4 bg-gradient-to-r from-pink-500/20 to-purple-600/20 hover:from-pink-500/30 hover:to-purple-600/30 border border-pink-500/30 rounded-xl transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <CheckCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4 text-left">
                    <p className="font-semibold text-white">Creator or Business Account</p>
                    <p className="text-xs text-gray-300">I already have a Professional account</p>
                  </div>
                </button>

                <button
                  onClick={handlePersonalAccountHelp}
                  className="w-full flex items-center p-4 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-4 text-left">
                    <p className="font-semibold text-white">Personal Account</p>
                    <p className="text-xs text-gray-300">I'll switch to Creator (it's free!)</p>
                  </div>
                </button>

                <button
                  onClick={() => setShowAccountTypeModal(false)}
                  className="w-full p-3 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-App Browser Warning (simple version with arrow) */}
      {showInAppBrowserModal && (
        <div className="fixed inset-0 z-50" onClick={() => setShowInAppBrowserModal(false)}>
          {/* Arrow pointing to 3 dots menu */}
          <div className="absolute top-2 right-4 flex flex-col items-end animate-bounce">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
            </svg>
          </div>
          
          {/* Message bubble */}
          <div className="absolute top-16 right-4 left-4 sm:left-auto sm:w-72">
            <div className="bg-gray-900 rounded-2xl shadow-2xl border border-white/20 p-4">
              <p className="text-white text-sm font-medium mb-2">
                Tap the ⋯ menu above
              </p>
              <p className="text-gray-300 text-xs mb-3">
                Then select "Open in {isAndroid() ? 'Chrome' : 'Safari'}" for the best experience
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInAppBrowserModal(false);
                }}
                className="text-gray-400 text-xs hover:text-white transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Personal Account Info Modal - Switch to Creator */}
      {showPersonalInfoModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/80 backdrop-blur-sm" onClick={() => setShowPersonalInfoModal(false)} />
            
            <div className="relative inline-block w-full max-w-lg p-6 overflow-hidden text-left align-middle transition-all transform bg-white/10 backdrop-blur-lg border border-white/20 shadow-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowPersonalInfoModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  Join Millions of Creators
                </h3>
                <p className="text-sm text-gray-300">
                  Top influencers and serious content creators use Creator accounts for powerful insights and tools. Upgrade in 30 seconds - it's completely free!
                </p>
              </div>

              {/* Step-by-step guide with screenshots */}
              <div className="space-y-4 mb-6">
                {/* Step 1 */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm font-medium text-white mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">1</span>
                    Instagram will ask you to switch to Professional
                  </p>
                  <img 
                    src="/instagram_switch_to_professional.jpeg" 
                    alt="Instagram switch to professional prompt" 
                    className="w-full max-w-xs mx-auto rounded-lg border border-white/20 shadow-lg"
                  />
                </div>

                {/* Step 2 */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm font-medium text-white mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">2</span>
                    Select "Creator" when asked
                  </p>
                  <div className="relative w-full max-w-xs mx-auto">
                    <img 
                      src="/instagram_creator_or_business.jpeg" 
                      alt="Instagram creator or business selection" 
                      className="w-full rounded-lg border border-white/20 shadow-lg"
                    />
                    {/* Green indicator circle on Creator option */}
                    <div className="absolute top-[28%] right-[8%] w-8 h-8 border-4 border-green-400 rounded-full animate-pulse"></div>
                    <div className="absolute top-[28%] right-[8%] w-8 h-8 flex items-center justify-center">
                      <CheckCircleIcon className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info text */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-200">
                  <strong>Why Creators choose this:</strong> Access to analytics and insights, professional tools, and the ability to automatically backup your content with Vaulto. Plus, you can switch back anytime - your followers and content stay exactly the same.
                </p>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <button
                  onClick={handlePersonalAccountContinue}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all"
                >
                  Continue to Instagram
                </button>

                <button
                  onClick={() => setShowPersonalInfoModal(false)}
                  className="w-full px-4 py-3 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Affiliate Application Pending Banner */}
        {user?.affiliate_application_status === 'PENDING' && (
          <div className="bg-amber-900/50 backdrop-blur-lg border border-amber-500 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Affiliate Application Pending
                  </h3>
                  <p className="text-white/80 text-sm">
                    Your affiliate application is being reviewed. We'll notify you once it's approved.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/affiliates')}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                View Status
              </button>
            </div>
          </div>
        )}

        {/* Abandoned Flow Recovery Banner */}
        {showAbandonedFlowBanner && (
          <div className="bg-gradient-to-r from-pink-900/50 to-purple-900/50 backdrop-blur-lg border border-pink-500/50 rounded-xl p-4 sm:p-6 mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/30">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {t('wizard.abandonedFlow.title')}
                  </h3>
                  <p className="text-white/80 text-sm mt-1">
                    {t('wizard.abandonedFlow.message')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setShowAbandonedFlowBanner(false);
                    sessionStorage.setItem('abandoned_banner_dismissed', 'true');
                  }}
                  className="px-4 py-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
                >
                  {t('wizard.abandonedFlow.dismiss')}
                </button>
                <button
                  onClick={() => {
                    setShowAbandonedFlowBanner(false);
                    setShowAccountTypeModal(true);
                  }}
                  className="flex-1 sm:flex-none px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg text-sm font-semibold transition-all shadow-lg flex items-center justify-center"
                >
                  {t('wizard.abandonedFlow.continue')}
                  <ArrowRightIcon className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unified Dashboard Header */}
        
        {/* Facebook Page Connection Error Banner */}
        {facebookPageError && (
          <div className="bg-red-900/50 backdrop-blur-lg border border-red-600 rounded-xl p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">
                  Facebook Page Connection Required
                </h3>
                <p className="text-white/90 mb-4">
                  Your Instagram Creator account must be connected to a Facebook Page to use Vaulto. 
                  This is a requirement from Instagram for all third-party integrations.
                </p>
                <div className="bg-red-800/30 rounded-lg p-4 mb-4">
                  <h4 className="text-white font-medium mb-2">Step-by-step instructions:</h4>
                  <ol className="text-white/80 space-y-2 list-decimal list-inside">
                    <li>Create a Facebook Page (if you don't have one): Go to <a href="https://facebook.com/pages/create" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">facebook.com/pages/create</a></li>
                    <li>Open Instagram app → Settings → Account → Linked accounts</li>
                    <li>Select "Facebook" and sign in if needed</li>
                    <li>Choose the Facebook Page you want to link</li>
                    <li>Confirm the connection</li>
                    <li>Return to Vaulto and try connecting Instagram again</li>
                  </ol>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate('/settings')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Go to Settings
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setFacebookPageError(false)}
                    className="inline-flex items-center px-4 py-2 border border-red-600 text-sm font-medium rounded-lg text-red-300 hover:bg-red-800/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            {/* Google Drive Reconnection Warning - Show when there's a scope/permission error */}
            {oauthStatus?.google_connection_error && (
              <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-2xl p-4 sm:p-6">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">Google Drive Needs Reconnection</h3>
                    <p className="text-sm text-white/80 mb-3">
                      Your Google Drive connection is missing the required permissions. When reconnecting, make sure to check this box:
                    </p>
                    <img 
                      src="/google-drive-permission.png" 
                      alt="Google Drive permission checkbox" 
                      className="max-w-sm rounded-lg border border-white/20 shadow-lg mb-4"
                    />
                    <p className="text-sm text-white/80 mb-4">
                      Use a regular browser (Chrome, Safari) - not Facebook or Instagram's in-app browser.
                    </p>
                    <button
                      onClick={() => navigate('/settings')}
                      className="inline-flex items-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      Go to Settings to Reconnect
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Connection Status */}
            {oauthStatus?.instagram_connected && oauthStatus?.google_connected && !oauthStatus?.google_connection_error && (
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="inline-flex items-center space-x-3 bg-green-500/10 rounded-2xl px-4 py-2 border border-green-500/20">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    <span className="text-sm font-medium text-green-300">{t('dashboard.readyForBackup')}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs sm:text-sm text-gray-400">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      <span>Instagram</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      <span>Google Drive</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Getting Started Instructions - Show when accounts are not fully connected or when free user needs to start backup */}
            {/* Full Onboarding: Show when accounts not connected OR connected but no backups yet */}
            {(!oauthStatus?.instagram_connected || !oauthStatus?.google_connected || (user?.subscription_tier === 'free' && oauthStatus?.instagram_connected && oauthStatus?.google_connected && (!stats || stats.backed_up_posts === 0))) && (
              <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4 sm:p-6">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 flex-shrink-0 mt-0.5 hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 sm:hidden mb-2">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                      <h3 className="text-base font-semibold text-white">{t('dashboard.getStarted.title')}</h3>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3 hidden sm:block">{t('dashboard.getStarted.title')}</h3>
                    <p className="text-sm sm:text-base text-white/90 mb-4">
                      {t('dashboard.getStarted.description')}
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      {/* Step 1: Connect Google Drive (swapped - now first) */}
                      <div className={`p-3 sm:p-4 rounded-xl ${
                        !oauthStatus?.google_connected 
                          ? 'bg-white/5 border border-white/10' 
                          : 'bg-green-500/10 border border-green-500/20'
                      }`}>
                        <div className="flex items-start sm:items-center space-x-3">
                          {oauthStatus?.google_connected ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white text-sm sm:text-base">
                              {t('dashboard.getStarted.step1.title')}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                              {oauthStatus?.google_connected 
                                ? t('dashboard.getStarted.step1.connected')
                                : t('dashboard.getStarted.step1.description')}
                            </p>
                          </div>
                        </div>
                        {!oauthStatus?.google_connected && (
                          <button
                            onClick={() => handleConnectGoogle()}
                            className="mt-3 w-full sm:w-auto flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg transform hover:scale-105 transition-all duration-200"
                          >
                            {t('dashboard.getStarted.connectGoogleDrive')}
                          </button>
                        )}
                      </div>
                      
                      {/* Step 2: Connect Instagram (swapped - now second) */}
                      <div className={`p-3 sm:p-4 rounded-xl ${
                        !oauthStatus?.instagram_connected 
                          ? 'bg-white/5 border border-white/10' 
                          : 'bg-green-500/10 border border-green-500/20'
                      }`}>
                        <div className="flex items-start sm:items-center space-x-3">
                          {oauthStatus?.instagram_connected ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white text-sm sm:text-base">
                              {t('dashboard.getStarted.step2.title')}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                              {oauthStatus?.instagram_connected 
                                ? t('dashboard.getStarted.step2.connected')
                                : t('dashboard.getStarted.step2.description')}
                            </p>
                          </div>
                        </div>
                        {!oauthStatus?.instagram_connected && (
                          <>
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <button
                                onClick={handleConnectInstagramClick}
                                disabled={!oauthStatus?.google_connected}
                                className={`w-full sm:w-auto flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white ${
                                  oauthStatus?.google_connected
                                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 shadow-lg transform hover:scale-105'
                                    : 'bg-gray-600 cursor-not-allowed opacity-60'
                                } transition-all duration-200`}
                              >
                                {t('dashboard.getStarted.connectInstagram')}
                              </button>
                              <div className="flex items-center px-2.5 py-1.5 bg-green-500/10 rounded-full border border-green-500/30">
                                <ShieldCheckIcon className="h-3.5 w-3.5 text-green-400 mr-1.5" />
                                <span className="text-xs text-green-300 font-medium">Official Instagram API</span>
                              </div>
                            </div>
                            {!oauthStatus?.google_connected && (
                              <div className="flex items-center mt-2 text-yellow-400">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="text-xs font-medium">Connect Google Drive first</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      
                      {/* Step 3: Start Backup */}
                      <div className={`p-3 sm:p-4 rounded-xl ${
                        oauthStatus?.instagram_connected && oauthStatus?.google_connected
                          ? 'bg-green-500/10 border border-green-500/20' 
                          : 'bg-white/5 border border-white/10'
                      }`}>
                        <div className="flex items-start sm:items-center space-x-3">
                          {oauthStatus?.instagram_connected && oauthStatus?.google_connected ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white text-sm sm:text-base">
                              {t('dashboard.getStarted.step3.title')}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                              {oauthStatus?.instagram_connected && oauthStatus?.google_connected
                                ? t('dashboard.getStarted.step3.ready')
                                : t('dashboard.getStarted.step3.description')}
                            </p>
                          </div>
                        </div>
                        {oauthStatus?.instagram_connected && oauthStatus?.google_connected && user?.subscription_tier === 'free' && (
                          <button
                            onClick={handleStartSync}
                            disabled={syncLoading}
                            className="mt-3 w-full sm:w-auto group flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transform hover:scale-105 transition-all duration-200"
                          >
                            {syncLoading ? (
                              <>
                                <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                {t('common.loading')}
                              </>
                            ) : (
                              <>
                                <PlayIcon className="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform" />
                                {t('dashboard.getStarted.step3.title')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <button
                        onClick={() => navigate('/settings')}
                        className="text-sm text-gray-400 hover:text-gray-300 underline transition-colors"
                      >
                        {t('dashboard.getStarted.goToSettings')}
                      </button>
                      <span className="text-xs sm:text-sm text-gray-400">
                        {/* Step order: 1=Google Drive, 2=Instagram */}
                        {!oauthStatus?.google_connected && t('dashboard.getStarted.step1.description')}
                        {oauthStatus?.google_connected && !oauthStatus?.instagram_connected && t('dashboard.getStarted.step2.description')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Main Content */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white">{t('dashboard.welcome')}</h1>
                <p className="text-gray-300 mt-2">{user?.full_name || user?.email}</p>
                
                {/* Subscription Status for Premium/Pro */}
                {user?.subscription_tier && ['premium', 'pro'].includes(user.subscription_tier) && (
                  <div className="mt-4">
                    <div className="inline-flex items-center space-x-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/30 text-green-300 border border-green-500/30">
                        {user.subscription_tier.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-300">{t('dashboard.subscription.automaticBackup')}</span>
                    </div>
                    <div className="flex items-center mt-2 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      <span>{t('dashboard.subscription.nextSync', { minutes: stats?.next_sync_in || '< 10' })}</span>
                    </div>
                    {stats?.last_sync && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t('dashboard.subscription.lastSync', { date: formatDate(stats.last_sync) })}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Free User Status */}
                {user?.subscription_tier === 'free' && (
                  <div className="mt-3 flex items-center gap-3 text-sm text-gray-400">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-700/50 text-gray-300 text-xs font-medium">
                      FREE
                    </span>
                    <span>{t('dashboard.subscription.manualBackup', { remaining: stats?.remaining_posts ?? 100 })}</span>
                  </div>
                )}
              </div>
              
              {/* Auto-backup Status Indicator for Premium/Pro */}
              {user?.subscription_tier && ['premium', 'pro'].includes(user.subscription_tier) && (
                <div className="mt-6 sm:mt-0">
                  <div className="text-right">
                    <div className="inline-flex items-center space-x-2 bg-green-500/20 rounded-xl px-4 py-3 border border-green-500/30">
                      <div className="animate-pulse h-2 w-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm font-medium text-green-300">{t('dashboard.subscription.autoBackupRunning')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid - Moved above Instagram Accounts */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <PhotoIcon className="h-8 w-8 text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-300">{t('dashboard.stats.totalPosts')}</p>
                  <p className="text-2xl font-bold text-white">{stats.total_posts}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-300">{t('dashboard.stats.backedUp')}</p>
                  <p className="text-2xl font-bold text-white">{stats.backed_up_posts}</p>
                  {user?.subscription_tier === 'free' && stats.remaining_posts !== undefined && (
                    <p className="text-xs text-gray-400 mt-1">
                      {stats.remaining_posts} {t('dashboard.stats.remaining')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-8 w-8 text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-300">{t('dashboard.stats.pending')}</p>
                  <p className="text-2xl font-bold text-white">{stats.pending_backups}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CloudArrowUpIcon className="h-8 w-8 text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-300">{t('dashboard.stats.storageUsed')}</p>
                  <p className="text-2xl font-bold text-white">
                    {formatBytes(stats.total_storage_used)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Free User Backup Status Banner */}
        {user?.subscription_tier === 'free' && stats && stats.total_posts > 0 && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-pink-500/20 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-yellow-500/30 shadow-2xl">
              <div className="flex flex-col gap-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    {stats.limit_reached ? (
                      <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400 mr-2 flex-shrink-0" />
                    ) : (
                      <CloudArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400 mr-2 flex-shrink-0" />
                    )}
                    <h3 className="text-base sm:text-lg font-semibold text-white">
                      {stats.limit_reached 
                        ? t('dashboard.freeTier.limitReached')
                        : t('dashboard.freeTier.title')
                      }
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base text-white/90">
                      <span className="font-semibold">{stats.backed_up_posts} {t('dashboard.freeTier.backedUp')}</span>
                      {stats.total_unbacked_posts > 0 && !stats.limit_reached && (
                        <span className="block sm:inline sm:ml-2 mt-1 sm:mt-0">
                          • <span className="font-semibold text-yellow-300">{stats.total_unbacked_posts} {t('dashboard.freeTier.waiting')}</span>
                        </span>
                      )}
                    </p>
                    {stats.limit_reached && stats.total_unbacked_posts > 0 && (
                      <p className="text-xs sm:text-sm text-yellow-200">
                        {t('dashboard.freeTier.upgradeMessage', { total: stats.total_posts })}
                      </p>
                    )}
                    {!stats.limit_reached && stats.remaining_posts !== undefined && (
                      <p className="text-xs sm:text-sm text-gray-300">
                        {t('dashboard.freeTier.remainingMessage', { remaining: stats.remaining_posts })}
                      </p>
                    )}
                  </div>
                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="bg-white/10 rounded-full h-2.5 sm:h-3 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-yellow-500 to-orange-500 h-full transition-all duration-500"
                        style={{ width: `${Math.min(100, (stats.backed_up_posts / 100) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>{stats.backed_up_posts} backed up</span>
                      <span>100 limit</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {/* Show Backup Now button if user hasn't reached limit and has unbacked posts */}
                  {!stats.limit_reached && stats.total_unbacked_posts > 0 && (
                    <button
                      onClick={handleStartSync}
                      disabled={syncLoading || !oauthStatus?.instagram_connected || !oauthStatus?.google_connected}
                      className="inline-flex items-center justify-center px-4 py-2.5 border border-yellow-500 text-sm font-medium rounded-xl text-yellow-300 hover:bg-yellow-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {syncLoading ? (
                        <>
                          <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-yellow-300 border-t-transparent rounded-full"></div>
                          {t('dashboard.freeTier.backingUp')}
                        </>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                          {t('dashboard.freeTier.backupNow')}
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center justify-center px-5 py-2.5 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-semibold rounded-xl text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl"
                  >
                    {t('dashboard.freeTier.upgradeNow')}
                    <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Multiple Instagram Accounts Manager for PRO/AGENCY Users */}
        {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency') && (
          <div className="mb-8">
            <InstagramAccountsManager 
              selectedAccountId={selectedAccountId}
              onAccountSelect={setSelectedAccountId}
            />
          </div>
        )}

        {/* Content Restore - Subtle link for Premium+ Users (emergency use only) */}
        {['premium', 'pro', 'agency'].includes(user?.subscription_tier) && stats?.backed_up_posts > 0 && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => navigate('/restore')}
              className="inline-flex items-center text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              <ArrowUpTrayIcon className="h-3.5 w-3.5 mr-1.5" />
              Need to restore content to a new account?
            </button>
          </div>
        )}

        {/* New Posts Indicator - Only show for premium/pro users OR for free users who DON'T have the Free Tier Banner visible */}
        {/* For free users, this is redundant with the Free Tier Backup Status section above */}
        {/* So we only show it for premium/pro users who have new unbacked posts */}
        {user?.subscription_tier !== 'free' && newPostsData?.has_new_posts && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-purple-500/30 shadow-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start sm:items-center">
                  <div className="flex-shrink-0 mr-3 sm:mr-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
                      <ArrowPathIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-white">
                      {newPostsData.new_posts_count > 0 
                        ? t('dashboard.newPosts.available', { count: newPostsData.new_posts_count, plural: newPostsData.new_posts_count > 1 ? 's' : '' })
                        : t('dashboard.newPosts.notBackedUp', { count: newPostsData.unbacked_posts_count, plural: newPostsData.unbacked_posts_count > 1 ? 's' : '' })
                      }
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-300 mt-1">
                      {t('dashboard.newPosts.description')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3">
                  <button
                    onClick={handleRefreshPosts}
                    disabled={refreshLoading}
                    className="inline-flex items-center justify-center px-4 py-2.5 border border-purple-500 text-sm font-medium rounded-xl text-purple-300 hover:bg-purple-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Check for new posts now"
                  >
                    {refreshLoading ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-purple-300 border-t-transparent rounded-full"></div>
                        Checking...
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2" />
                        {t('dashboard.newPosts.checkAgain')}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleStartSync}
                    disabled={syncLoading || !oauthStatus?.instagram_connected || !oauthStatus?.google_connected}
                    className="inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncLoading ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        {t('dashboard.newPosts.backingUp')}
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                        {t('dashboard.newPosts.backupNow')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Instagram Posts */}
        {console.log('Instagram section condition:', oauthStatus?.instagram_connected, oauthStatus)}
        {oauthStatus?.instagram_connected ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">{t('dashboard.instagramContent.title')}</h3>
                <p className="text-sm text-gray-300 mt-1">
                  {totalPosts > 0 ? t('dashboard.instagramContent.showing', { current: posts.length, total: totalPosts.toLocaleString() }) : t('dashboard.instagramContent.noPosts')}
                </p>
                {postsError && (
                  <p className="text-xs text-red-400 mt-1">{t('dashboard.instagramContent.failedToLoad')}: {postsError.message}</p>
                )}
              </div>
              
              {/* Search and Filter Controls */}
              <div className="flex flex-col gap-3 mt-4 sm:mt-0 w-full sm:w-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleRefreshPosts}
                    disabled={refreshLoading}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-xl text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('dashboard.instagramContent.title')}
                  >
                    {refreshLoading ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2" />
                        {t('common.refresh')}
                      </>
                    )}
                  </button>
                  
                  <div className="relative flex-1 sm:flex-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder={t('dashboard.instagramContent.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <div className="relative flex-1 sm:flex-none">
                    <FunnelIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    <select
                      value={filterType}
                      onChange={(e) => handleFilterChange(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-8 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent appearance-none [&>option]:text-gray-900 [&>option]:bg-white text-sm"
                    >
                      <option value="all">{t('dashboard.instagramContent.allMedia')}</option>
                      <option value="image">{t('dashboard.instagramContent.photosOnly')}</option>
                      <option value="video">{t('dashboard.instagramContent.videosOnly')}</option>
                    </select>
                  </div>
                  
                  <div className="relative flex-1 sm:flex-none">
                    <select
                      value={contentType}
                      onChange={(e) => handleContentTypeChange(e.target.value)}
                      className="w-full sm:w-auto px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent appearance-none [&>option]:text-gray-900 [&>option]:bg-white text-sm"
                    >
                      <option value="all">{t('dashboard.instagramContent.allTypes')}</option>
                      <option value="posts">{t('dashboard.instagramContent.posts')}</option>
                      <option value="reels">{t('dashboard.instagramContent.reels')}</option>
                      <option value="stories">{t('dashboard.instagramContent.stories')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {(() => {
              const showLoading = postsLoading || ((isInstagramJustConnected || isRecentlyConnected()) && totalPosts === 0 && !postsError);
              console.log('Loading animation check:', {
                postsLoading,
                isInstagramJustConnected,
                isRecentlyConnected: isRecentlyConnected(),
                totalPosts,
                postsError,
                showLoading
              });
              return showLoading;
            })() ? (
              <div className="text-center py-12">
                {(isInstagramJustConnected || isRecentlyConnected()) ? (
                  <div className="max-w-md mx-auto">
                    <div className="relative mb-6">
                      <div className="animate-pulse">
                        <div className="w-16 h-16 mx-auto bg-gradient-to-r from-pink-400 to-purple-400 rounded-full flex items-center justify-center">
                          <PhotoIcon className="h-8 w-8 text-white" />
                        </div>
                      </div>
                      <div className="absolute inset-0 animate-ping">
                        <div className="w-16 h-16 mx-auto bg-gradient-to-r from-pink-400 to-purple-400 rounded-full opacity-20"></div>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2">
                      {t('dashboard.instagramContent.fetching')}
                    </h4>
                    <p className="text-sm text-gray-300 mb-4">
                      {t('dashboard.instagramContent.fetchingDescription')}
                    </p>
                    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                      <p className="text-xs text-gray-400">
                        {t('dashboard.instagramContent.fetchingNote')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400 mx-auto"></div>
                    <p className="mt-2 text-gray-300">{t('dashboard.instagramContent.loadingPosts')}</p>
                  </>
                )}
              </div>
            ) : postsError ? (
              <div className="text-center py-12">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <p className="text-gray-300 text-lg">{t('dashboard.instagramContent.failedToLoad')}</p>
                <p className="text-sm text-gray-400 mt-2">{postsError.message}</p>
                <button
                  onClick={() => refetchPosts()}
                  className="mt-4 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
                >
                  {t('dashboard.instagramContent.tryAgain')}
                </button>
              </div>
            ) : posts.length > 0 ? (
              <>
                {/* Posts Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 mb-8">
                  {posts.map((post, index) => (
                    <div 
                      key={post.id || index} 
                      className="relative group cursor-pointer"
                      onClick={() => handleMediaClick(post)}
                    >
                      <div className="aspect-square bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-200">
                        {/* Backup Status Indicator */}
                        {getBackupStatusIndicator(post)}
                        
                        {/* Content Type Badge */}
                        <div className="absolute top-2 left-2 z-10 flex items-center space-x-1 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1">
                          {getContentTypeIcon(post.media_type)}
                          <span className="text-xs text-white font-medium">{getContentTypeLabel(post.media_type)}</span>
                        </div>

                        {/* Video Play Button Overlay */}
                        {isVideo(post.media_type) && (
                          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                            <div className="bg-black/70 rounded-full p-3 group-hover:bg-black/90 transition-colors">
                              <PlayIcon className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        )}

                        {/* Media Display */}
                        {post.thumbnail_url || post.media_url ? (
                          <img 
                            src={post.thumbnail_url || post.media_url} 
                            alt={post.caption?.substring(0, 50) || `Instagram ${post.media_type}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            onError={(e) => {
                              // If image fails to load (likely due to expired URL), try to fetch fresh URL
                              if (!e.target.dataset.retried) {
                                e.target.dataset.retried = 'true';
                                // Use our proxy endpoint to get fresh URL
                                const freshUrl = `/api/sync/instagram-media/${post.instagram_id}`;
                                e.target.src = freshUrl;
                              } else {
                                // If retry also fails, show placeholder
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }
                            }}
                          />
                        ) : null}
                        <div className="w-full h-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center" style={{display: (post.thumbnail_url || post.media_url) ? 'none' : 'flex'}}>
                          {getContentTypeIcon(post.media_type, 'h-8 w-8')}
                        </div>
                        
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-colors duration-200 rounded-xl flex items-end justify-start opacity-0 group-hover:opacity-100">
                          <div className="text-white p-3 w-full">
                            <p className="text-xs font-medium mb-1">
                              {formatDate(post.timestamp)}
                            </p>
                            {post.like_count > 0 && (
                              <p className="text-xs text-gray-300">
                                ❤️ {post.like_count}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                    >
                      <ChevronLeftIcon className="h-4 w-4 mr-1" />
                      {t('common.previous')}
                    </button>
                    
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 7) {
                          pageNum = i + 1;
                        } else if (currentPage <= 4) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 3) {
                          pageNum = totalPages - 6 + i;
                        } else {
                          pageNum = currentPage - 3 + i;
                        }
                        
                        const isCurrentPage = pageNum === currentPage;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                              isCurrentPage
                                ? 'bg-pink-500 text-white'
                                : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                    >
                      {t('common.next')}
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-300 text-lg">{t('dashboard.instagramContent.noPostsFound')}</p>
                <p className="text-sm text-gray-400 mt-2">
                  {searchQuery ? t('dashboard.instagramContent.tryDifferentSearch') : t('dashboard.instagramContent.noPostsDescription')}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Instagram Content Placeholder - Not Connected */
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white">{t('dashboard.instagramContent.notConnected.title')}</h3>
                <p className="text-sm text-gray-300 mt-1">
                  {t('dashboard.instagramContent.notConnected.subtitle')}
                </p>
              </div>

              {/* Placeholder Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8 w-full opacity-60">
                {[...Array(12)].map((_, index) => (
                  <div key={index} className="aspect-square rounded-xl overflow-hidden border border-white/10">
                    <div className={`w-full h-full flex items-center justify-center ${
                      index % 4 === 0 ? 'bg-gradient-to-br from-pink-500/30 to-purple-500/30' :
                      index % 4 === 1 ? 'bg-gradient-to-br from-purple-500/30 to-blue-500/30' :
                      index % 4 === 2 ? 'bg-gradient-to-br from-orange-500/30 to-pink-500/30' :
                      'bg-gradient-to-br from-blue-500/30 to-teal-500/30'
                    }`}>
                      {index % 3 === 0 ? (
                        <PhotoIcon className="h-8 w-8 text-white/40" />
                      ) : index % 3 === 1 ? (
                        <FilmIcon className="h-8 w-8 text-white/40" />
                      ) : (
                        <RectangleStackIcon className="h-8 w-8 text-white/40" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Connect Message */}
              <div className="max-w-md">
                <p className="text-gray-300 mb-6">
                  {t('dashboard.instagramContent.notConnected.description')}
                </p>
                <button
                  onClick={handleConnectInstagramClick}
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  {t('dashboard.instagramContent.notConnected.connectButton')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={() => setSelectedVideo(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
            
            <div className="bg-black rounded-xl overflow-hidden shadow-2xl">
              <video
                src={selectedVideo.media_url}
                controls
                autoPlay
                className="w-full h-auto max-h-[80vh]"
                poster={selectedVideo.thumbnail_url}
                onError={(e) => {
                  // If video fails to load, try fresh URL
                  if (!e.target.dataset.retried) {
                    e.target.dataset.retried = 'true';
                    const freshUrl = `/api/sync/instagram-media/${selectedVideo.instagram_id}`;
                    e.target.src = freshUrl;
                  }
                }}
              >
                Your browser does not support the video tag.
              </video>
              
              <div className="p-4 bg-gray-900 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-300">
                    {formatDate(selectedVideo.timestamp)}
                  </span>
                  {selectedVideo.like_count > 0 && (
                    <span className="text-sm text-gray-300">
                      ❤️ {selectedVideo.like_count}
                    </span>
                  )}
                </div>
                {selectedVideo.caption && (
                  <p className="text-sm text-gray-200 line-clamp-3">
                    {selectedVideo.caption}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Welcome Modal */}
      {showUpgradeWelcome && upgradedTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-purple-900/95 via-pink-900/95 to-orange-900/95 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl max-w-md w-full transform transition-all">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center">
                {upgradedTier.toLowerCase() === 'pro' ? (
                  <TrophyIcon className="h-12 w-12 text-yellow-400" />
                ) : (
                  <SparklesIcon className="h-12 w-12 text-purple-400" />
                )}
                <div className="ml-4">
                  <h2 className="text-2xl font-bold text-white">Welcome! 🎉</h2>
                  <p className="text-sm text-gray-300 mt-1">Your plan has been upgraded</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowUpgradeWelcome(false);
                  setUpgradedTier(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
                <p className="text-white font-semibold mb-2">
                  You're now on the <span className="text-purple-300">{upgradedTier}</span> plan!
                </p>
                <div className="space-y-2 text-sm text-gray-200">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                    <span>Your remaining posts are now being backed up automatically</span>
                  </div>
                  {upgradedTier.toLowerCase() === 'pro' ? (
                    <>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                        <span>Unlimited posts backup</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                        <span>Up to 3 Instagram accounts</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                        <span>Advanced analytics & custom schedules</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                        <span>Up to 5,000 posts backup</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                        <span>Automatic backups every 10 minutes</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                        <span>Priority support</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <div className="bg-green-500/20 rounded-xl p-3 border border-green-500/30">
                <p className="text-sm text-green-200 flex items-center">
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Backup process is running automatically in the background
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowUpgradeWelcome(false);
                  setUpgradedTier(null);
                }}
                className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all transform hover:scale-105"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer variant="dark" />

      {/* Onboarding Wizard Modal */}
      {showOnboardingWizard && (
        <OnboardingWizard
          oauthStatus={oauthStatus}
          onComplete={handleWizardComplete}
          onSkip={handleWizardSkip}
          syncLoading={syncLoading}
          onStartSync={handleStartSync}
        />
      )}

      {/* Google Drive Instructions Modal */}
      {showGoogleInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-gray-900 rounded-3xl shadow-2xl max-w-md w-full p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">Connect Google Drive</h3>
            
            <div className="space-y-4 mb-6">
              <p className="text-gray-300">
                Before connecting your Google Drive, please note:
              </p>
              
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <p className="text-sm text-orange-300 font-medium mb-3">
                  ⚠️ Important: When Google asks for permissions, make sure this checkbox is checked:
                </p>
                <img 
                  src="/google-drive-permission.png" 
                  alt="Google Drive permission checkbox" 
                  className="w-full rounded-lg border border-white/20 shadow-lg"
                />
              </div>
              
              <p className="text-gray-300 text-sm">
                This allows Vaulto to create a dedicated folder for your Instagram backups without accessing your other files.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowGoogleInstructions(false)}
                className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowGoogleInstructions(false);
                  proceedWithGoogleConnection();
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl hover:from-blue-600 hover:to-green-600 transition-colors font-medium shadow-lg"
              >
                Continue to Google
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-App Browser Suggestion Popup (simple version with arrow) */}
      {showInAppBrowserSuggestion && (
        <div className="fixed inset-0 z-[120]" onClick={() => setShowInAppBrowserSuggestion(false)}>
          {/* Arrow pointing to 3 dots menu */}
          <div className="absolute top-2 right-4 flex flex-col items-end animate-bounce">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
            </svg>
          </div>
          
          {/* Message bubble */}
          <div className="absolute top-16 right-4 left-4 sm:left-auto sm:w-72">
            <div className="bg-gray-900 rounded-2xl shadow-2xl border border-white/20 p-4">
              <p className="text-white text-sm font-medium mb-2">
                Tap the ⋯ menu above
              </p>
              <p className="text-gray-300 text-xs mb-3">
                Then select "Open in {isAndroid() ? 'Chrome' : 'Safari'}" for the best experience
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInAppBrowserSuggestion(false);
                }}
                className="text-gray-400 text-xs hover:text-white transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Explanation Modal - Shows before redirecting to Instagram OAuth */}
      {showInstagramExplanationModal && (
        <div className="fixed inset-0 z-[120] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/70 backdrop-blur-sm" onClick={() => setShowInstagramExplanationModal(false)} />
            
            <div className="relative inline-block overflow-hidden text-left align-bottom transition-all transform bg-gray-900 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:max-w-md w-full border border-white/20">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">
                    {t('dashboard.instagramExplanation.title')}
                  </h3>
                  <button
                    onClick={() => setShowInstagramExplanationModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-5">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30 mb-4">
                    <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  
                  <p className="text-white text-center text-2xl sm:text-3xl font-bold mb-4">
                    {t('dashboard.instagramExplanation.heading')}
                  </p>
                  
                  <p className="text-white/90 text-center text-base sm:text-lg mb-5 leading-relaxed">
                    {t('dashboard.instagramExplanation.description')}
                  </p>
                </div>

                {/* Permission Screen Preview */}
                <div className="mb-4">
                  <p className="text-white/70 text-xs font-medium mb-2 text-center">
                    {t('dashboard.instagramExplanation.permissionPreview')}
                  </p>
                  <div className="bg-white rounded-xl p-3 shadow-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-900 font-semibold text-xs">Vaulto</p>
                        <p className="text-gray-500 text-[10px]">wants to access your account</p>
                      </div>
                    </div>
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center space-x-2 text-gray-700 text-xs">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        <span>Access profile information</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-700 text-xs">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        <span>Access media and content</span>
                      </div>
                    </div>
                    <div className="bg-blue-500 text-white text-center py-1.5 rounded-lg text-xs font-medium">
                      Allow
                    </div>
                    <p className="text-center text-gray-400 text-[10px] mt-1.5">
                      {t('dashboard.instagramExplanation.permissionNote')}
                    </p>
                  </div>
                </div>

                {/* Safety Badge */}
                <div className="mb-5 p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="flex items-center space-x-1 bg-green-500/20 px-2 py-1 rounded-full">
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      <span className="text-green-300 text-xs font-medium">{t('dashboard.instagramExplanation.readOnlyBadge')}</span>
                    </div>
                  </div>
                  <p className="text-white/70 text-xs text-center mt-2">
                    {t('dashboard.instagramExplanation.neverModify')}
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={proceedWithInstagramOAuth}
                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all shadow-lg"
                  >
                    {t('dashboard.instagramExplanation.continue')}
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </button>

                  <button
                    onClick={() => setShowInstagramExplanationModal(false)}
                    className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Drive Required Modal - Shows when user tries to connect Instagram without Google Drive */}
      {showGoogleDriveRequiredModal && (
        <div className="fixed inset-0 z-[120] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/70 backdrop-blur-sm" onClick={() => setShowGoogleDriveRequiredModal(false)} />
            
            <div className="relative inline-block overflow-hidden text-left align-bottom transition-all transform bg-gray-900 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:max-w-md w-full border border-white/20">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">
                    {t('dashboard.googleDriveRequired.title')}
                  </h3>
                  <button
                    onClick={() => setShowGoogleDriveRequiredModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-5">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 via-green-400 to-yellow-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-4">
                    <img src="/google_drive_icon_.png" alt="Google Drive" className="h-8 w-8" />
                  </div>
                  
                  <p className="text-white text-center text-lg font-medium mb-3">
                    {t('dashboard.googleDriveRequired.heading')}
                  </p>
                  
                  <p className="text-gray-300 text-center text-sm mb-4">
                    {t('dashboard.googleDriveRequired.description')}
                  </p>
                </div>

                <div className="mb-5 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <CloudArrowUpIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-blue-200 text-sm font-medium mb-1">
                        {t('dashboard.googleDriveRequired.whyTitle')}
                      </p>
                      <p className="text-gray-300 text-sm">
                        {t('dashboard.googleDriveRequired.whyDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setShowGoogleDriveRequiredModal(false);
                      handleConnectGoogle();
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-semibold rounded-xl transition-all shadow-lg"
                  >
                    {t('dashboard.googleDriveRequired.connectButton')}
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </button>

                  <button
                    onClick={() => setShowGoogleDriveRequiredModal(false)}
                    className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Abandonment Survey Modal - z-[120] to appear above wizard (z-[100]) */}
      {showSurveyModal && (
        <div className="fixed inset-0 z-[120] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/70 backdrop-blur-sm" onClick={handleSurveySkip} />
            
            <div className="relative inline-block overflow-hidden text-left align-bottom transition-all transform bg-gray-900 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:max-w-lg w-full border border-white/20">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">
                    Help us improve
                  </h3>
                  <button
                    onClick={handleSurveySkip}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <p className="text-gray-300 mb-4">
                  We noticed you started connecting your Instagram but didn't finish. What happened?
                </p>
                
                <div className="space-y-2 mb-4">
                  {SURVEY_OPTIONS.map((option) => (
                    <label
                      key={option.id}
                      className={`flex items-center p-3 rounded-xl cursor-pointer transition-all ${
                        surveyReason === option.id
                          ? 'bg-pink-500/20 border-2 border-pink-500'
                          : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="radio"
                        name="survey_reason"
                        value={option.id}
                        checked={surveyReason === option.id}
                        onChange={(e) => setSurveyReason(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                        surveyReason === option.id
                          ? 'border-pink-500 bg-pink-500'
                          : 'border-gray-400'
                      }`}>
                        {surveyReason === option.id && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className="text-white text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-400 text-sm mb-2">
                    Any additional comments? (optional)
                  </label>
                  <textarea
                    value={surveyComment}
                    onChange={(e) => setSurveyComment(e.target.value)}
                    placeholder="Tell us more..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleSurveySkip}
                    className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleSurveySubmit}
                    disabled={surveySubmitting || !surveyReason}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {surveySubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 